import { createContext, useContext, useState } from 'react';
import PropTypes from 'prop-types';

import { trans } from '@spotahome/soyuz/client';

import { AGE, NATIONALITY, OCCUPATION } from '../constants/seoCharts';

const DECIMAL_DIGITS = 2;

const defaultFn = () => {};

const SECTIONS_MAPPING = {
  ageDistribution: AGE,
  occupationType: OCCUPATION,
  nationality: NATIONALITY
};

const UNKNOWN_LABEL = 'unknown';

const getLabel = (item, section) => {
  if (section === NATIONALITY) {
    return item.countryName;
  }
  if (section === AGE && item.type.toLowerCase() === UNKNOWN_LABEL) {
    return trans('chart.label.unknown');
  }
  if (section === OCCUPATION) {
    return trans(`chart.label.${item.type.toLowerCase()}`);
  }

  return item.type;
};

const normalizeData = (data, section) => {
  return data?.map(item => {
    return {
      ...item,
      label: getLabel(item, section),
      percentage: parseFloat(item.percentage).toFixed(DECIMAL_DIGITS)
    };
  });
};

const sortByAge = (a, b) => {
  const aAge = parseInt(a.label.split('-')[0].replace(/\+$/, ''), 10);
  const bAge = parseInt(b.label.split('-')[0].replace(/\+$/, ''), 10);
  return aAge - bAge;
};

const normalizeSections = data => {
  const normalizedSections = {};

  Object.keys(SECTIONS_MAPPING).forEach(section => {
    const mappedSection = SECTIONS_MAPPING[section];
    normalizedSections[mappedSection] = normalizeData(
      data[section],
      mappedSection
    );
  });

  normalizedSections[AGE]?.sort(sortByAge);

  return normalizedSections;
};

const TenantProfileStatisticsContext = createContext({
  tenantProfileStatistics: [],
  topAgeRange: '',
  topOccupation: '',
  topCountry: '',
  section: '',
  setSection: defaultFn
});

export const TenantProfileStatisticsProvider = ({
  children,
  cityId,
  tenantProfileStatistics: initialTenantProfileStatistics = []
}) => {
  const [currentSection, setCurrentSection] = useState(NATIONALITY);
  const allTenantProfileStatistics = normalizeSections(
    initialTenantProfileStatistics
  );

  const setSection = newSection => {
    if (newSection !== currentSection) {
      setCurrentSection(newSection);
    }
  };

  const sortFn = (a, b) => b.percentage - a.percentage;

  const getTop = section => {
    const sectionData = allTenantProfileStatistics[section]
      ? [...allTenantProfileStatistics[section]]
      : [];
    sectionData.sort(sortFn);
    return sectionData?.[0]?.label ?? '';
  };

  return (
    <TenantProfileStatisticsContext.Provider
      value={{
        cityId,
        tenantProfileStatistics:
          allTenantProfileStatistics[currentSection] ?? [],
        section: currentSection,
        topAgeRange: getTop(AGE),
        topOccupation: getTop(OCCUPATION),
        topCountry: getTop(NATIONALITY),
        setSection
      }}
    >
      {children}
    </TenantProfileStatisticsContext.Provider>
  );
};

TenantProfileStatisticsProvider.propTypes = {
  children: PropTypes.oneOfType([
    PropTypes.arrayOf(PropTypes.node),
    PropTypes.node
  ]).isRequired,
  cityId: PropTypes.string.isRequired,
  tenantProfileStatistics: PropTypes.arrayOf(
    PropTypes.shape({
      month: PropTypes.string,
      year: PropTypes.string,
      averagePrice: PropTypes.number,
      delta: PropTypes.number
    })
  )
};

export const useTenantProfileStatisticsContext = () =>
  useContext(TenantProfileStatisticsContext);

export default {
  TenantProfileStatisticsProvider,
  useTenantProfileStatisticsContext
};
